// Unsaved-change tracking and the one-button save for the Assemblies Manager.
//
// Until 2026-10-05 the page had TWO saves: "Save Changes" (POST/PATCH
// /assemblies — the header, incl. the Join Station) and a separate "Save
// Operations" (PUT /assemblies/{id}/processes — the rows that actually create
// the join task). Assembly #128 CONCRETEFORMBRACKET was saved with its Welding
// row typed into the grid but never saved: Join Station read Welding, the
// operations list was empty, and SO-1134's jobs would have run Laser → Brake →
// Shipping with no weld task and its coil inserts never taken out of stock.
// Now one Save writes both, and nothing typed can be lost without a warning.

// Values the form can hold for one field, normalized so "5", 5 and "5.0"
// compare equal and null / undefined / "" all mean blank.
const txt = (v) => (v === null || v === undefined ? "" : String(v));
const num = (v) => {
  const s = txt(v).trim();
  if (s === "") return "";
  const n = Number(s);
  return Number.isFinite(n) ? String(n) : s;
};

// [key, label shown in the unsaved-changes list, normalizer]
const HEADER_SPEC = [
  ["assemblyName", "Assembly Name", txt],
  ["assemblyCustomerId", "Customer", txt],
  ["assemblyNotes", "Notes", txt],
  ["assemblyPrice", "System Price", num],
  ["assemblyPrimaryPartProdId", "Primary Part", txt],
  ["primaryPartQuantity", "Primary Part Qty", num],
  ["joinStep", "Join Station", txt],
  ["buildSetQuantity", "Build in Sets of", num],
  ["shipLengthIn", "Ship Length", num],
  ["shipWidthIn", "Ship Width", num],
  ["shipHeightIn", "Ship Height", num],
  ["shipWeightLbs", "Ship Weight", num],
  ["shipStackable", "Stackable", (v) => (v === false ? "no" : "yes")],
  ["shipMaxStackLayers", "Max Stack Layers", num],
];

// The step the join is stored as: a Fabrication sub-process ("Welding") or
// the station itself ("Brake"). Matches what Save sends as join_workstation.
export function joinStepOf(joinWorkstation, joinSubProcess) {
  return joinWorkstation === "Fabrication" ? txt(joinSubProcess) : txt(joinWorkstation);
}

// The header form as one comparable object. `fields` holds the page's raw
// state values (joinWorkstation + joinSubProcess, not joinStep).
export function normalizeHeader(fields) {
  const f = { ...fields, joinStep: joinStepOf(fields.joinWorkstation, fields.joinSubProcess) };
  const out = {};
  HEADER_SPEC.forEach(([key, , norm]) => { out[key] = norm(f[key]); });
  return out;
}

// Labels of the header fields that differ between two normalizeHeader() results.
export function changedHeaderLabels(saved, current) {
  if (!saved || !current) return [];
  return HEADER_SPEC.filter(([key]) => saved[key] !== current[key]).map(([, label]) => label);
}

// The empty "Create New Assembly" form.
export const BLANK_HEADER_FIELDS = Object.freeze({
  assemblyName: "", assemblyCustomerId: "", assemblyNotes: "", assemblyPrice: "",
  assemblyPrimaryPartProdId: "", primaryPartQuantity: 1, joinWorkstation: "", joinSubProcess: "",
  buildSetQuantity: "", shipLengthIn: "", shipWidthIn: "", shipHeightIn: "", shipWeightLbs: "",
  shipStackable: true, shipMaxStackLayers: "",
});

// Every field of every operation row, in order. Unlike the page's cost
// signature (step + minutes + coating $ only), notes, vendor and colour count:
// they are saved too, so editing them is an unsaved change.
export function processRowsSignature(rows) {
  return JSON.stringify((rows || []).map((r) => [
    txt(r.step), num(r.minutes), num(r.coatCost), txt(r.coatVendor),
    txt(r.coatColorCode), txt(r.coatColorName), txt(r.coatColorHex), txt(r.notes),
  ]));
}

// API process rows → the grid's row shape.
export function gridRowsFromApi(procs) {
  return (Array.isArray(procs) ? procs : []).map((p) => ({
    step: p.sub_process || p.workstation,
    minutes: p.est_minutes_per_assembly != null ? String(p.est_minutes_per_assembly) : "",
    coatCost: p.coating_cost_per_assembly != null ? String(p.coating_cost_per_assembly) : "",
    coatVendor: p.coating_vendor_id || "",
    coatColorCode: p.coating_color_code || "",
    coatColorName: p.coating_color_name || "",
    coatColorHex: p.coating_color_hex || "",
    notes: p.notes || "",
  }));
}

// Grid rows → the PUT /assemblies/{id}/processes row list.
export function opsRowsPayload(rows, fabricationSubProcesses) {
  return (rows || []).map((r) => {
    const isFab = fabricationSubProcesses.includes(r.step);
    const isCoating = r.step === "Coating";
    return {
      workstation: isFab ? "Fabrication" : r.step,
      sub_process: isFab ? r.step : null,
      est_minutes_per_assembly: isCoating ? null : (parseFloat(r.minutes) || 0),
      coating_cost_per_assembly: isCoating ? (parseFloat(r.coatCost) || 0) : null,
      coating_vendor_id: isCoating && r.coatVendor ? parseInt(r.coatVendor, 10) : null,
      coating_color_code: isCoating ? (txt(r.coatColorCode).trim() || null) : null,
      coating_color_name: isCoating ? (txt(r.coatColorName).trim() || null) : null,
      coating_color_hex: isCoating ? (txt(r.coatColorHex).trim() || null) : null,
      notes: r.notes || null,
    };
  });
}

// The grid checks PUT /assemblies/{id}/processes would make, as the sentence
// to show, or null when the rows can be saved.
export function validateOpsRows(rows, joinStep) {
  const list = rows || [];
  const blankStep = list.findIndex((r) => !r.step);
  if (blankStep !== -1) return `Operation row ${blankStep + 1} needs a step selected.`;
  const matches = list.filter((r) => r.step === joinStep).length;
  if (matches !== 1) {
    return `Exactly one operation row must be the join step '${joinStep}' (found ${matches}). `
      + "Add it to Assembly Operations, or change the Join Station.";
  }
  for (let i = 0; i < list.length; i += 1) {
    const r = list[i];
    if (r.step === "Coating") {
      // A Coating row with no cost makes the quote drop the part's own coating
      // cost while contributing nothing itself: the coating vanishes silently.
      if (!(parseFloat(r.coatCost) > 0)) {
        return `Operation row ${i + 1}: Coating needs a cost per assembly. Leaving it blank would drop the coating from the quote.`;
      }
    } else if (num(r.minutes) === "") {
      // The API requires minutes on every non-Coating row; a blank used to be
      // sent as 0, which schedules the step with no time.
      return `Operation row ${i + 1} (${r.step}): enter the minutes per assembly (0 is allowed).`;
    }
  }
  return null;
}

// What one press of Save writes. The header is always sent (as the old Save
// Changes did). Operations are sent when the grid has rows and they, or the
// join, changed — or on a brand-new assembly.
export function planAssemblySave({ isNew, opsChanged, joinChanged, rowCount, savedRowCount }) {
  return {
    saveOps: rowCount > 0 && (isNew || opsChanged || joinChanged),
    // PUT needs the join row, so the last row can't be removed from here.
    opsCleared: rowCount === 0 && savedRowCount > 0,
    noOps: rowCount === 0 && savedRowCount === 0,
  };
}

export function noOpsWarning(name, joinStep) {
  const what = name ? `"${name}"` : "This assembly";
  const step = joinStep || "join";
  return `${what} has no Assembly Operations.\n\n`
    + `Jobs built from it will get NO ${step} task, and its purchased components will never be taken out of stock.\n\n`
    + `Add the ${step} step under Assembly Operations, or press OK to save without it.`;
}

export const OPS_CLEARED_MESSAGE = "An assembly keeps at least its join operation, so the last operation row can't be removed. "
  + "Add the join step back (or press Discard to undo).";

// What is unsaved, in the words the warnings use.
export function unsavedItems(headerLabels, opsChanged) {
  return [...(headerLabels || []), ...(opsChanged ? ["Assembly Operations"] : [])];
}

export function unsavedChangesMessage(items, action) {
  return `You have unsaved changes: ${items.join(", ")}.\n\n`
    + `${action ? `${action} will discard them. ` : ""}Press Cancel to go back and Save Changes, or OK to discard them.`;
}

// Is this click a same-tab, in-app navigation away from the current page?
// Plain left clicks on a router link only: modifier/middle clicks open a new
// tab, and a link to another site unloads the page, which beforeunload covers.
export function isLeavingPageClick(event, anchor, location) {
  if (!anchor || !location) return false;
  if (event.button !== undefined && event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  const target = anchor.getAttribute("target");
  if (target && target !== "_self") return false;
  if (anchor.hasAttribute("download")) return false;
  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#") || /^(javascript|mailto|tel):/i.test(href)) return false;
  let url;
  try { url = new URL(href, location.href); } catch { return false; }
  if (url.origin !== location.origin) return false;
  return url.pathname !== location.pathname || url.search !== location.search;
}
